<template>
  <div
    class="form-group slim-size"
    :class="{
      'has-icon': !!icon,
      'has-value': !!value,
      'is-date': isDate
    }"
  >
    <label :for="id" v-if="label && !checkbox && type !== 'file'">
      {{ label }}
      <span v-if="required" class="text-negative-700">*</span>
    </label>
    <i v-if="icon" :class="'icon ' + icon"></i>

    <div>
      <slot name="after-label" />
    </div>

    <div
      v-if="select"
      class="select-wrapper"
      :class="{ 'has-suffix': suffixLabel }"
    >
      <el-select
        :multiple="multiple"
        :id="id"
        :slim="slim"
        :ultraSlim="ultraSlim"
        :label="label"
        v-model="innerValue"
        :class="classObject"
        :disabled="disabled"
        :placeholder="placeholder"
        :autocomplete="disableAutocomplete ? 'off' : remote ? '' : 'off'"
        :filterable="filterable"
        :remote="remote"
        :remote-method="remoteMethod"
        :loading="loading"
        :clearable="nullable"
        @change="v => $emit('change', v)"
      >
        <el-option
          v-for="item in options"
          :key="item.id"
          :label="item.name"
          :value="item.id"
        />
      </el-select>
      <div
        v-if="suffixLabel"
        class="select-suffix btn fs-14 text-primary"
        @click="selectSuffixAction"
      >
        <i class="fa-solid fa-plus mr-3" />
        <span class="d-none d-lg-block">{{ suffixLabel }}</span>
      </div>
    </div>

    <b-form-checkbox
      v-else-if="checkbox"
      class="d-block"
      v-model="innerValue"
      :id="id"
      :class="classObject"
    >
      {{ label }}
    </b-form-checkbox>

    <b-form-checkbox-group
      v-else-if="checkboxes"
      v-model="innerValue"
      :id="id"
      :class="classObject"
    >
      <b-form-checkbox
        v-for="(option, index) in options"
        :key="index"
        :value="option.id"
        class="d-block"
      >
        {{ optionLabelFunction(option) }}
      </b-form-checkbox>
    </b-form-checkbox-group>
    <the-mask
      v-else-if="mask.length"
      :mask="mask"
      :masked="masked"
      v-model="innerValue"
      class="form-control"
      :class="classObject"
      :id="id"
      :type="type"
      :maxLength="maxLength"
      :placeholder="placeholder"
      :autocomplete="disableAutocomplete ? 'off' : ''"
      :autocorrect="disableAutocapitalize ? 'off' : ''"
      :autocapitalize="disableAutocapitalize ? 'none' : ''"
      :readonly="readonly"
      :disabled="disabled"
    />
    <div
      v-else-if="isSwitch"
      class="custom-control custom-switch"
      :class="{ disabled }"
    >
      <input
        type="checkbox"
        v-model="innerValue"
        class="custom-control-input"
        :id="id"
        :label="label"
        :disabled="disabled"
      />
      <label class="custom-control-label" :for="id" />
    </div>
    <money
      v-else-if="isMoney"
      v-model="innerValue"
      class="form-control"
      v-bind="moneyParams"
      :class="classObject"
      :id="id"
      :type="type"
      :maxLength="maxLength"
      :placeholder="placeholder"
      :autocomplete="disableAutocomplete ? 'off' : ''"
      :autocorrect="disableAutocapitalize ? 'off' : ''"
      :autocapitalize="disableAutocapitalize ? 'none' : ''"
      :readonly="readonly"
      :disabled="disabled"
    />
    <vue-tel-input
      v-else-if="isPhone"
      v-model="innerValue"
      class="form-control"
      :class="classObject"
      :id="id"
      type="tel"
      :input-options="{ placeholder }"
      :readonly="readonly"
      :disabled="disabled"
      @country-changed="countryChanged"
      @validate="obj => $emit('validate', obj)"
    />
    <textarea
      v-else-if="textarea"
      v-model="innerValue"
      class="form-control"
      :class="classObject"
      :id="id"
      :type="type"
      :maxLength="maxLength"
      :placeholder="placeholder"
      :readonly="readonly"
      :disabled="disabled"
      :rows="rows"
    />
    <div class="form-wrapper" v-else-if="type === 'password'">
      <input
        v-model="innerValue"
        class="form-control password"
        :class="classObject"
        :id="id"
        :type="!showPassword && type"
        :placeholder="placeholder"
        :autocomplete="disableAutocomplete ? 'off' : ''"
        :autocorrect="disableAutocapitalize ? 'off' : ''"
        :autocapitalize="disableAutocapitalize ? 'none' : ''"
        :readonly="readonly"
        :disabled="disabled"
        @blur="blur"
      />
      <div
        :class="
          showError || showInnerError ? 'show-error-icon' : 'show-password-icon'
        "
      >
        <button
          v-if="!(showError || showInnerError)"
          type="button"
          class="clear-button pt-2"
          @click.stop="showPassword = !showPassword"
        >
          <i
            :class="
              !(showPassword || showInnerError) ? 'icon-eye' : 'icon-eye-off'
            "
          />
        </button>
        <div v-else>
          <i class="fa-solid fa-circle-exclamation" />
        </div>
      </div>
    </div>

    <div class="radio-group text-dark" v-else-if="type === 'radio'">
      <b-form-group v-slot="{ ariaDescribedby }">
        <b-form-radio
          class="mb-2"
          :class="classObject"
          v-for="option in options"
          :key="option.name"
          :id="`${option.id}`"
          :aria-describedby="ariaDescribedby"
          :value="option.id"
          v-model="innerValue"
        >
          {{ option.name }}
        </b-form-radio>
      </b-form-group>
    </div>

    <div class="d-flex align-items-center" v-else-if="isInputPrefix">
      <span class="font-weight-bold pr-3">{{ prefix }}</span>
      <input
        v-model="innerValue"
        class="form-control"
        :class="classObject"
        :id="id"
        :type="type"
        :maxLength="maxLength"
        :placeholder="placeholder"
        :autocomplete="disableAutocomplete ? 'off' : ''"
        :autocorrect="disableAutocapitalize ? 'off' : ''"
        :autocapitalize="disableAutocapitalize ? 'none' : ''"
        :readonly="readonly"
        :disabled="disabled"
      />
    </div>
    <div class="form-wrapper" v-else>
      <input
        v-model="innerValue"
        class="form-control"
        ref="input"
        :class="classObject"
        :id="id"
        :type="type"
        :maxLength="maxLength"
        :placeholder="placeholder"
        :autocomplete="disableAutocomplete ? 'off' : ''"
        :autocorrect="disableAutocapitalize ? 'off' : ''"
        :autocapitalize="disableAutocapitalize ? 'none' : ''"
        :readonly="readonly"
        :disabled="disabled"
        @keypress="preventNumericInput"
        @blur="blur"
      />
      <div v-if="showError || showInnerError" class="show-error-icon">
        <i class="fa-solid fa-circle-exclamation" />
      </div>

      <div v-if="isDate" class="date-picker-wrapper d-flex">
        <v-date-picker
          :first-day-of-week="1"
          v-model="date"
          :popover="{ placement: 'bottom', visibility: 'click' }"
          :mode="mode"
          :min-date="minDate"
          is24hr
        >
          <template v-slot="{ togglePopover }">
            <button
              type="button"
              class="btn text-secondary"
              :disabled="disabled"
              @click="togglePopover({ placement: 'auto-start' })"
            >
              <i class="icon-calendar"></i>
            </button>
          </template>
          <template v-slot:footer>
            <div class="p-2">
              <button
                type="button"
                class="btn btn-primary btn-sm w-100"
                @click="$refs.input.click()"
              >
                Fechar
              </button>
            </div>
          </template>
        </v-date-picker>
      </div>

      <button
        v-if="withClearButton && value && value.length"
        class="clear"
        @click="clear"
      >
        <i class="icon-x"></i>
      </button>
    </div>

    <div
      v-if="(showError && showErrorMessage) || showInnerError"
      class="invalid-feedback"
      :class="{ 'd-block': showError || showInnerError }"
    >
      {{ errors[0] }}
    </div>

    <button
      v-if="color"
      class="color-button"
      @click="showColorPicker = true"
      :style="`background-color: ${innerValue}`"
    />

    <div
      v-if="showColorPicker"
      class="color-picker"
      v-click-outside="hideColorPicker"
    >
      <chrome-picker @input="updateColor" :value="innerValue" />
    </div>
  </div>
</template>

<script>
import { Option, Select } from "element-ui";
import moment from "moment";
import vClickOutside from "v-click-outside";
import { Money } from "v-money";
import { Chrome } from "vue-color";
import { TheMask } from "vue-the-mask";

export default {
  components: {
    Money,
    "el-select": Select,
    "el-option": Option,
    "chrome-picker": Chrome,
    TheMask
  },
  directives: {
    clickOutside: vClickOutside.directive
  },
  props: {
    suffixLabel: {
      type: String,
      default: ""
    },
    noNumber: Boolean,
    label: {
      type: String,
      default: ""
    },
    prefix: {
      type: String,
      default: ""
    },
    nullable: {
      type: Boolean,
      default: false
    },
    filterable: {
      type: Boolean,
      default: false
    },
    remote: Boolean,
    remoteMethod: Function,
    loading: Boolean,
    mask: {
      type: [String, Array],
      default: ""
    },
    masked: {
      type: Boolean,
      default: false
    },
    isMoney: {
      type: Boolean,
      default: false
    },
    isInputPrefix: {
      type: Boolean,
      default: false
    },
    isDate: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: "date"
    },
    isPhone: {
      type: Boolean,
      default: false
    },
    minNumber: {
      type: Number,
      default: 0
    },
    maxNumber: {
      type: Number,
      default: 999999999999
    },
    select: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    checkbox: {
      type: Boolean,
      default: false
    },
    checkboxes: {
      type: Boolean,
      default: false
    },
    textarea: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array,
      default: () => []
    },
    optionLabelFunction: {
      type: Function,
      default: item => item.name
    },
    icon: {
      type: String,
      default: ""
    },
    type: {
      type: String,
      default: "text"
    },
    placeholder: {
      type: String,
      default: ""
    },
    id: {
      type: String,
      required: true
    },
    value: {
      type: [String, Number, Boolean, Array],
      default: ""
    },
    customClass: {
      type: String,
      default: ""
    },
    showErrorMessage: {
      type: Boolean,
      default: true
    },
    errors: {
      type: Array,
      default: () => []
    },
    disableAutocapitalize: {
      type: Boolean,
      default: false
    },
    disableAutocomplete: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    withClearButton: {
      type: Boolean,
      default: false
    },
    isLogin: {
      type: Boolean,
      default: false
    },
    rows: {
      type: String,
      default: ""
    },
    isSwitch: {
      type: Boolean,
      default: false
    },
    slim: {
      type: Boolean,
      default: false
    },
    ultraSlim: {
      type: Boolean,
      default: false
    },
    color: {
      type: Boolean,
      default: false
    },
    moneyConfig: {
      type: Object,
      default: () => {}
    },
    maxLength: {
      type: Number,
      default: 524288
    },
    min: {
      type: [Number, String],
      default: 0
    },
    max: {
      type: [Number, String],
      default: 99999999999999999
    },
    minDate: {
      type: Date,
      default: null
    },
    setFocusOnError: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    autoCheck: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showColorPicker: false,
      showPassword: false,
      showError: this.errors ? this.errors.length : 0,
      showInnerError: false,
      innerValue: this.value,
      date: null
    };
  },
  computed: {
    moneyParams() {
      return {
        precision: 2,
        prefix: `R$ `,
        decimal: ",",
        thousands: ".",
        ...this.moneyConfig
      };
    },
    classObject() {
      const result = {
        "is-invalid": this.showError || this.showInnerError,
        "slim-size": this.slim,
        "ultra-slim-size": this.ultraSlim
      };
      result[this.customClass] = true;
      return result;
    }
  },
  watch: {
    date(newVal) {
      let date = moment(newVal)
        .format()
        .split("T")[0]
        .split("-")
        .reverse()
        .join("/");
      if (this.mode === "dateTime") {
        date += " " + `${newVal.getHours()}`.padStart(2, "0");
        date += ":" + `${newVal.getMinutes()}`.padStart(2, "0");
      }
      this.innerValue = date;
    },
    errors(newVal) {
      if (newVal && !!newVal[0]) {
        this.showError = true;
      } else {
        this.showError = false;
      }
    },
    innerValue(newVal, oldVal) {
      if (newVal != this.value) {
        this.showError = false;
        if (this.type.toLowerCase() == "number") {
          if (newVal >= this.minNumber && newVal <= this.maxNumber) {
            this.$emit("input", newVal);
          } else {
            this.$emit("input", oldVal);
            this.innerValue = oldVal;
          }
        } else {
          this.$emit("input", newVal);
        }

        if (this.isDate && newVal && newVal.length === 10) {
          this.date = new Date(
            newVal
              .split("/")
              .reverse()
              .join("-") + "T12:00:00"
          );
        }
      }
      if (this.isPhone && newVal.match(/[a-zA-Z]/)) {
        setTimeout(() => {
          this.innerValue = newVal.replace(/[a-zA-Z]/, "");
        }, 2);
      }
    },
    value(newVal) {
      if (this.innerValue !== newVal) {
        this.innerValue = newVal;
        this.showError = false;
      }
    }
  },
  methods: {
    selectSuffixAction() {
      this.$emit("selectSuffixAction");
    },
    async blur() {
      if (!this.innerValue) return;
      this.innerValue = this.innerValue + " ";
      this.innerValue = await this.innerValue.trim();

      if (!this.autoCheck) return;

      if (!this.innerValue.length) {
        this.$emit("setError", ["Este campo não pode ser em branco."], this.id);
        this.showInnerError = true;
        return;
      } else if (this.type == "email") {
        let regExp = /^[\w-\.]+@([\w-]+\.)+[\w-]/;
        if (!this.innerValue.match(regExp)) {
          this.$emit("setError", ["Insira um email válido."], this.id);
          this.showInnerError = true;
          return;
        }
      } else if (this.type == "password" && this.innerValue.length < 6) {
        this.$emit(
          "setError",
          ["Certifique-se de que este campo tenha mais de 6 caracteres."],
          this.id
        );
        this.showInnerError = true;
        return;
      }

      this.$emit("setError", [""], this.id);
      this.showInnerError = false;
    },
    preventNumericInput($event) {
      const keyCode = $event.keyCode ? $event.keyCode : $event.which;
      if (this.noNumber && keyCode > 47 && keyCode < 58) {
        $event.preventDefault();
      }
    },
    updateColor(obj) {
      this.innerValue = obj.hex;
    },
    hideColorPicker() {
      this.showColorPicker = false;
    },
    countryChanged(obj) {
      this.$emit("country-changed", obj);
      setTimeout(() => {
        this.innerValue = document.querySelector(`#${this.id} input`).value;
      }, 1);
    },
    clear() {
      this.$emit("input", "");
    }
  },
  mounted() {
    if (this.errors.length && this.setFocusOnError)
      document.getElementById(this.id).focus();
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

label > span {
  color: #d0000ee0;
}
.form-group {
  position: relative;
  margin-bottom: 0;
  padding: 8px 0;

  .select-wrapper {
    position: relative;
    .select-suffix {
      position: absolute;
      right: 36px;
      top: calc(50% - 11px);
      padding: 0;
      min-height: 0;
      font-weight: 500;
      // color: #5200cde5 !important;
      @media screen and (max-width: 991px) {
        // i {
        //   color: #420098;
        //   font-weight: 400;
        // }
        right: 26px;
        top: calc(50% - 15px);
        .fa-plus {
          padding: 6px;
          border-radius: 4px;
          border: 1px solid #a967ff;
        }
      }
    }
  }
  ::v-deep .has-suffix {
    .el-select__tags {
      max-width: calc(100% - 220px) !important;
      @media screen and (max-width: 991px) {
        max-width: calc(100% - 80px) !important;
      }
    }
  }

  label {
    color: var(--l-neutral-45, rgba(87, 88, 107, 0.88));
  }

  .form-control {
    border-radius: 4px;
    padding: 8px 8px 8px 12px;
    font-size: 14px;
    background: #fff;
    &.password {
      padding-right: 53px !important;
    }
    &:disabled {
      background: #f7f7f7f7;
      opacity: 0.5;
    }

    &::placeholder {
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
    }

    &::-webkit-scrollbar {
      background-color: transparent;
      border-radius: 0 7px 7px 0;
      width: 3px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: var(--l-neutral-7, rgba(59, 59, 74, 0.9));
    }
  }

  .slim-size {
    min-height: 28px;
    max-height: 50px;
  }

  .ultra-slim-size {
    min-height: 30px;
    max-height: 40px;
  }

  select.form-control {
    background: white;
    appearance: none;
    background-image: url("data:image/svg+xml;utf8,<svg fill='black' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/><path d='M0 0h24v24H0z' fill='none'/></svg>");
    background-repeat: no-repeat;
    background-position-x: 100%;
    background-position-y: center;
    padding-right: 20px;
  }
  &.has-icon {
    .icon {
      position: absolute;
      line-height: 54px;
      left: 4px;
    }

    .form-control {
      padding-right: 32px;
    }
  }

  .clear {
    position: absolute;
    height: 64px;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    background: transparent;
    border: none;
  }

  &:not(.has-value) {
    select {
      color: #aeb6c4 !important;
    }
  }

  ::v-deep .vti__dropdown-list {
    z-index: 3;
  }

  .date-picker-wrapper {
    position: absolute;
    top: 5px;
    left: 0;
    z-index: 1;

    .icon-calendar {
      font-size: 20px;
      padding-bottom: 2px;
    }
  }

  &.is-date {
    .form-control {
      padding-left: 40px;
      border-radius: 16px;
      background-color: #fefefe;
    }
    .clear {
      padding: 0;
      font-size: 14px;
      height: 16px;
      right: 11px;
      color: #495057;
      border-radius: 50%;
      border: 1px solid #495057;
    }
  }

  .form-wrapper {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    position: relative;

    .form-control {
      padding-right: 7px;
    }
    .show-password-icon {
      position: absolute;
      right: 25px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 17px;
      cursor: pointer;
      user-select: none;
    }
    .show-error-icon {
      position: absolute;
      right: 25px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 17px;
      user-select: none;
      i {
        color: #ff3f34;
      }
    }
    .clear-button {
      background-color: transparent;
      padding: 0px;
      margin: 0px;
      border: 0px;
    }
  }

  input[type="checkbox"].form-control {
    transform: scale(1.1);
    min-height: 10px;
  }

  input[type="checkbox"]:not(.custom-switch) {
    height: 10px;
    cursor: pointer;
    font-size: 17px;
    visibility: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  input[type="checkbox"]:not(.custom-switch):after {
    content: " ";
    background-color: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
    color: $primary;
    width: 16px;
    height: 16px;
    visibility: visible;
    border: 1px solid #928f9890;
    border-radius: 4px;
  }

  input[type="checkbox"]:not(.custom-switch):checked:after {
    content: url(../assets/images/checked-icon.svg);
    background-color: $primary;
    font-size: 10px;
    color: #fff;
    font-weight: bold;
  }

  .color-button {
    position: absolute;
    top: 49px;
    right: 8px;
    min-height: 28px;
    min-width: 28px;
    max-height: 28px;
    max-width: 28px;
    border-radius: 28px;
    align-items: center;
    border: none;
    background-color: black;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .color-picker {
    position: absolute;
    right: 0;
    z-index: 90;
  }

  textarea {
    resize: both;
    min-height: 100px !important;
    max-height: 200px !important;
    resize: none;
  }

  .custom-switch .custom-control-label::after {
    width: calc(1.25rem - 4px);
    height: calc(1.25rem - 4px);
    left: calc(-2.08rem + 5px);
    top: calc(0.3rem + 1.6px);
    transition: transform 0.15s ease-in-out, background-color 0.15s ease-in-out,
      border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    border-radius: 50%;
  }

  &:not(.has-value) .custom-switch .custom-control-label::after {
    left: calc(-2.08rem + 1px);
  }

  .custom-switch .custom-control-label::before {
    height: 1.3rem;
    width: 2.4rem;
    border-radius: 2.5rem;
  }

  .custom-control-input:checked ~ .custom-control-label::before {
    background-image: linear-gradient(
      148.5deg,
      rgba($color: #ee6469, $alpha: 1) -19.31%,
      rgba($color: #5576ec, $alpha: 1) 100%
    ) !important;
    border-color: transparent;
  }

  .custom-switch.disabled {
    opacity: 0.5;

    label {
      cursor: not-allowed;
    }
  }

  ::v-deep .vue-tel-input {
    display: flex;

    input {
      background: transparent;
    }
  }

  textarea {
    resize: none;
  }
}
</style>
